import { useBreakpoints, useMounted } from '@vueuse/core';
import { computed, type ComputedRef, type Ref } from 'vue';

export enum Breakpoint {
  M = 'm',
  L = 'l',
  Xl = 'xl',
}

export const BREAKPOINT_VALUE: Record<Breakpoint, number> = {
  [Breakpoint.M]: 1001,
  [Breakpoint.L]: 1240,
  [Breakpoint.Xl]: 1440,
};

const PRERENDER_WIDTH = BREAKPOINT_VALUE[Breakpoint.Xl];

export interface ViewportState {
  current: Breakpoint | null;
  isActive(breakpoint: Breakpoint): boolean;
  isSmaller(breakpoint: Breakpoint): boolean;
  isSmallerOrEqual(breakpoint: Breakpoint): boolean;
  isInBetween(startBreakpoint: Breakpoint, endBreakpoint: Breakpoint): boolean;
  isGreater(breakpoint: Breakpoint): boolean;
  isGreaterOrEqual(breakpoint: Breakpoint): boolean;
}

export function resolveActiveBreakpoint(width: number): Breakpoint | null {
  return (Object.keys(BREAKPOINT_VALUE) as Breakpoint[])
    .sort((a, b) => BREAKPOINT_VALUE[a] - BREAKPOINT_VALUE[b])
    .reduce<Breakpoint | null>((active, bp) => (width >= BREAKPOINT_VALUE[bp] ? bp : active), null);
}

export function getViewportState(width: number): ViewportState {
  const current = resolveActiveBreakpoint(width);
  return {
    current,
    isActive: (bp) => current === bp,
    isSmaller: (bp) => width < BREAKPOINT_VALUE[bp],
    isSmallerOrEqual: (bp) => width <= BREAKPOINT_VALUE[bp],
    isInBetween: (start, end) => width >= BREAKPOINT_VALUE[start] && width < BREAKPOINT_VALUE[end],
    isGreater: (bp) => width > BREAKPOINT_VALUE[bp],
    isGreaterOrEqual: (bp) => width >= BREAKPOINT_VALUE[bp],
  };
}

export function useViewport() {
  const breakpoints = useBreakpoints(BREAKPOINT_VALUE);
  const mounted = useMounted();
  const prerenderState = getViewportState(PRERENDER_WIDTH);

  /**
   * До маунта (SSR + первый клиентский рендер) отдаём одинаковое статическое значение —
   * сервер и клиент рендерят идентичный HTML, гидрация проходит без рассинхрона.
   * После маунта переключаемся на реактивные значения matchMedia.
   */
  const hydrationSafe = (reactive: Ref<boolean>, staticValue: boolean): ComputedRef<boolean> =>
    computed(() => (mounted.value ? reactive.value : staticValue));

  const getCurrent = (): ComputedRef<Breakpoint | null> => {
    const active = breakpoints.active();
    return computed(() => (mounted.value ? ((active.value || null) as Breakpoint | null) : prerenderState.current));
  };

  const isActive = (breakpoint: Breakpoint) => {
    return getCurrent().value === breakpoint;
  };

  const isSmaller = (breakpoint: Breakpoint) => {
    return hydrationSafe(breakpoints.smaller(breakpoint), prerenderState.isSmaller(breakpoint));
  };

  const isSmallerOrEqual = (breakpoint: Breakpoint) => {
    return hydrationSafe(breakpoints.smallerOrEqual(breakpoint), prerenderState.isSmallerOrEqual(breakpoint));
  };

  const isInBetween = (startBreakpoint: Breakpoint, endBreakpoint: Breakpoint) => {
    return hydrationSafe(
      breakpoints.between(startBreakpoint, endBreakpoint),
      prerenderState.isInBetween(startBreakpoint, endBreakpoint),
    );
  };

  const isGreater = (breakpoint: Breakpoint) => {
    return hydrationSafe(breakpoints.greater(breakpoint), prerenderState.isGreater(breakpoint));
  };

  const isGreaterOrEqual = (breakpoint: Breakpoint) => {
    return hydrationSafe(breakpoints.greaterOrEqual(breakpoint), prerenderState.isGreaterOrEqual(breakpoint));
  };

  return {
    getCurrent,
    isActive,
    isSmaller,
    isSmallerOrEqual,
    isInBetween,
    isGreater,
    isGreaterOrEqual,
  };
}
